import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { ResponsiveContainer, LineChart, BarChart, AreaChart, ComposedChart, Line, Bar, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';

function WeatherTooltip({ active, payload, label, type, t }) {
    if (!active || !payload?.length) return null

    const formatDate = (dateString) => {
        if (!dateString) return ''
        const parts = dateString.substring(0, 10).split('-')
        if (parts.length !== 3) return dateString
        const base = `${parts[2]}.${parts[1]}.${parts[0].slice(2)}`
        if (dateString.includes(' ')) return `${base} ${dateString.split(' ')[1]}` // hourly: append HH:MM
        return base
    }

    const unit = { temp: '°C', precipitation: ' mm', snowfall: ' cm', wind: ' km/h', humidity: '%' }[type] || ''
    const isHourly = String(label).includes(' ')
    const dateLabel = isHourly ? t('pages.dashboard.timestamp') : t('pages.dashboard.date')

    // sort by value descending so temp reads Max > Avg > Min and wind reads Gusts > Max > Avg (typical case)
    const sortedPayload = [...payload].sort((a, b) => {
        const av = typeof a.value === 'number' ? a.value : -Infinity
        const bv = typeof b.value === 'number' ? b.value : -Infinity
        return bv - av
    })

    // styled like detection's DomTooltip: dot + bold "name: value" rows, shared date line at bottom
    return (
        <Paper elevation={0} sx={{ p: '8px 10px', bgcolor: 'rgba(30,30,50,0.95)', color: 'white', borderRadius: '4px', boxShadow: '0 3px 6px rgba(0,0,0,0.3)' }}>
            {sortedPayload.map((elem, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.125 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: elem.color, flexShrink: 0 }} />
                    <Typography sx={{ fontWeight: 'bold', fontSize: 13 }}>
                        {elem.name}: {typeof elem.value === 'number' ? elem.value.toFixed(1) : elem.value}{unit}
                    </Typography>
                </Box>
            ))}
            <Typography sx={{ color: '#ccc', fontSize: 12, mt: 0.375 }}>
                {dateLabel}: {formatDate(label)}
            </Typography>
        </Paper>
    )
}

function WeatherSubChart({ data, type, t }) {
    const isHourly = data.resolution === 'hourly'

    const isMultiDay = useMemo(() => {
        if (!data?.dates?.length) return false
        const days = new Set(data.dates.map(elem => String(elem).substring(0, 10)))
        return days.size > 1
    }, [data?.dates])

    const chartData = useMemo(() => {
        return data.dates.map((value, index) => {
            const entry = { date: value }
            switch (type) {
                case 'temp':
                    if (isHourly) {
                        entry[t('pages.dashboard.temperature')] = data.temp[index]
                    } else {
                        entry[t('pages.dashboard.max')] = data.temp_max[index]
                        entry[t('pages.dashboard.average')] = data.temp_avg[index]
                        entry[t('pages.dashboard.min')] = data.temp_min[index]
                    }
                    break
                case 'precipitation':
                    entry[t('pages.dashboard.precipitation')] = data.precipitation[index]
                    break
                case 'snowfall':
                    entry[t('pages.dashboard.snowfall')] = data.snowfall[index]
                    break
                case 'wind':
                    if (isHourly) {
                        entry[t('pages.dashboard.speed')] = data.wind_speed[index]
                        entry[t('pages.dashboard.gusts')] = data.wind_gusts[index]
                    } else {
                        entry[t('pages.dashboard.average')] = data.wind_avg[index]
                        entry[t('pages.dashboard.max')] = data.wind_max[index]
                        entry[t('pages.dashboard.gusts')] = data.wind_gusts[index]
                    }
                    break
                case 'humidity':
                    entry[t('pages.dashboard.humidity')] = data.humidity[index]
                    break
                default:
                    break
            }
            return entry
        })
    }, [data, type, isHourly, t])

    // for multi-day hourly data (e.g. 2-14 day range with 24 points/day), pin one tick per day
    // (picks the first hourly sample of each day) so the x-axis reads "13.04, 14.04, ..." like the daily detection chart
    const explicitTicks = useMemo(() => {
        if (!isHourly || !isMultiDay || !data?.dates?.length) return undefined
        const seen = new Set()
        const ticks = []
        for (const dateStr of data.dates) {
            const day = String(dateStr).substring(0, 10)
            if (!seen.has(day)) {
                seen.add(day)
                ticks.push(dateStr)
            }
        }
        return ticks
    }, [data?.dates, isHourly, isMultiDay])

    const formatXAxis = (value) => {
        if (!value) return ''
        const dateString = String(value)
        // hourly single-day: show time only
        if (isHourly && !isMultiDay && dateString.includes(' ')) {
            return dateString.split(' ')[1]
        }
        // everything else (daily, or multi-day hourly): show date as DD.MM
        const parts = dateString.substring(0, 10).split('-')
        if (parts.length === 3) return `${parts[2]}.${parts[1]}`
        return dateString
    }

    const titles = {
        temp: t('pages.dashboard.temperature'),
        precipitation: t('pages.dashboard.precipitation'),
        snowfall: t('pages.dashboard.snowfall'),
        wind: t('pages.dashboard.wind_speed'),
        humidity: t('pages.dashboard.humidity'),
    }

    const units = { temp: '°C', precipitation: 'mm', snowfall: 'cm', wind: 'km/h', humidity: '%' }

    const renderChart = () => {
        const commonXAxis = <XAxis dataKey="date" tickFormatter={formatXAxis} angle={-45} textAnchor="end" height={isMobile ? 40 : 50} tick={{ fontSize: isMobile ? 9 : 11 }} minTickGap={isMobile ? 15 : 5} ticks={explicitTicks} />
        const commonYAxis = <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 32 : 52} />
        const commonGrid = <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        const commonTooltip = <Tooltip content={<WeatherTooltip type={type} t={t} />} isAnimationActive={false} />
        const chartMargin = { top: 5, right: isMobile ? 18 : 38, bottom: 5, left: 0 }

        switch (type) {
            case 'temp':
                if (isHourly) {
                    return (
                        <LineChart data={chartData} margin={chartMargin}>
                            {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                            <Line type="monotone" dataKey={t('pages.dashboard.temperature')} stroke="#e74c3c" strokeWidth={2} dot={false} isAnimationActive={false} />
                        </LineChart>
                    )
                }
                return (
                    <ComposedChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        <Area type="monotone" dataKey={t('pages.dashboard.max')} stroke="#e74c3c" fill="rgba(231,76,60,0.15)" strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Area type="monotone" dataKey={t('pages.dashboard.min')} stroke="#c0392b" fill="transparent" strokeWidth={2} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey={t('pages.dashboard.average')} stroke="#e67e22" strokeWidth={2} strokeDasharray="8 4" dot={false} isAnimationActive={false} />
                    </ComposedChart>
                )

            case 'precipitation':
                return (
                    <BarChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        <Bar dataKey={t('pages.dashboard.precipitation')} fill="rgba(52,152,219,0.7)" isAnimationActive={false} />
                    </BarChart>
                )

            case 'snowfall':
                return (
                    <BarChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        <Bar dataKey={t('pages.dashboard.snowfall')} fill="rgba(149,165,166,0.8)" isAnimationActive={false} />
                    </BarChart>
                )

            case 'wind':
                return (
                    <LineChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        {isHourly ? (
                            <>
                                <Line type="monotone" dataKey={t('pages.dashboard.speed')} stroke="#27ae60" strokeWidth={2} dot={false} isAnimationActive={false} />
                                <Line type="monotone" dataKey={t('pages.dashboard.gusts')} stroke="#e74c3c" strokeWidth={1.5} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
                            </>
                        ) : (
                            <>
                                <Line type="monotone" dataKey={t('pages.dashboard.average')} stroke="#27ae60" strokeWidth={2} dot={false} isAnimationActive={false} />
                                <Line type="monotone" dataKey={t('pages.dashboard.max')} stroke="#2ecc71" strokeWidth={2} dot={{ r: 2, fill: '#2ecc71' }} isAnimationActive={false} />
                                <Line type="monotone" dataKey={t('pages.dashboard.gusts')} stroke="#e74c3c" strokeWidth={1.5} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
                            </>
                        )}
                    </LineChart>
                )

            case 'humidity':
                return (
                    <AreaChart data={chartData} margin={chartMargin}>
                        {commonGrid}{commonXAxis}{commonYAxis}{commonTooltip}
                        <Area type="monotone" dataKey={t('pages.dashboard.humidity')} stroke="#8e44ad" fill="rgba(142,68,173,0.1)" strokeWidth={2} dot={false} isAnimationActive={false} />
                    </AreaChart>
                )

            default:
                return null
        }
    }

    return (
        <Paper elevation={0} sx={{ pt: isMobile ? 1.5 : 2, mb: isMobile ? 1 : 2, userSelect: 'none', '&:last-child': { mb: 0 }, '& :focus': { outline: 'none' }, '& *': { outline: 'none !important' }, boxShadow: '0 2px 4px rgba(0,0,0,0.08)', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', fontSize: isMobile ? 14 : undefined }}>{titles[type]} ({units[type]})</Typography>
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                {renderChart()}
            </ResponsiveContainer>
        </Paper>
    )
}

export default function WeatherCharts({ weatherData, streams }) {

    const { t } = useTranslation()

    if (streams.size === 0) {
        return (
            <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                {t('pages.dashboard.no_data')}
            </Typography>
        )
    }

    return (
        <Box>
            {streams.has('temp') && <WeatherSubChart data={weatherData} type="temp" t={t} />}
            {streams.has('precipitation') && <WeatherSubChart data={weatherData} type="precipitation" t={t} />}
            {streams.has('snowfall') && <WeatherSubChart data={weatherData} type="snowfall" t={t} />}
            {streams.has('wind') && <WeatherSubChart data={weatherData} type="wind" t={t} />}
            {streams.has('humidity') && <WeatherSubChart data={weatherData} type="humidity" t={t} />}
        </Box>
    )
}
